import React, { useEffect, useState } from "react";
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { EmailConfirmationModal } from './EmailConfirmationModal';
import { InitialPaymentModal } from './InitialPaymentModal';
import { ConfirmQuoteStep } from './ConfirmQuoteStep';
import { RequestInvoiceModal } from './RequestInvoiceModal';
import { MesaControlPanel } from './MesaControlPanel';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import { fetchStepStatus } from "./functionsStepperPanel";
import { useQuoteAdjustmentDraft } from '../../../../../Hooks/useQuoteAdjustmentDraft';

export const QuoteStepperPanel = ({ persona, toast, categoriasFaltantes, dataBuro, categories, activeStep, setActiveStep, onOpenRecotizacion, draftRefreshKey = 0, onDraftChange }) => {
    const [accordionOpen, setAccordionOpen] = useState(null); // 0 = abierto, null = cerrado
    const [mesaControlCollapsed, setMesaControlCollapsed] = useState(false);
    const [processId, setProcessId] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showInitialPaymentModal, setShowInitialPaymentModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);

    const [loading, setLoading] = useState(true);

    // Estados de cada paso: 'completed', 'in-progress', 'error', 'pending'
    const [stepStates, setStepStates] = useState({
        ocularVisit: 'pending',       // Estado del paso 0 (Nuevo)
        confirmQuote: 'pending',      // Estado del paso 1
        uploadReceipt: 'pending',     // Estado del paso 2
        requestInvoice: 'pending'     // Estado del paso 3
    });
    const [stepsData, setStepsData] = useState([]);

    // Draft detection for recotización badges
    const { getDraftsByBudget } = useQuoteAdjustmentDraft();
    const [activeDraft, setActiveDraft] = useState(null);

    // traer estatus actual de la base de datos
    useEffect(() => {

        setStepStates({
            confirmQuote: 'pending',
            uploadReceipt: 'pending',
            requestInvoice: 'pending'
        });
        setStepsData([]);
        setActiveStep(0);

        fetchStepStatus(persona, setStepStates, setStepsData, setLoading, setActiveStep, setProcessId);

    }, [persona]);

    // Check for active recotización drafts (refetches on draftRefreshKey bump)
    useEffect(() => {
        if (persona?.infoBudget?.budget_id) {
            getDraftsByBudget(persona.infoBudget.budget_id)
                .then(result => {
                    if (result?.success && result.data) {
                        const readyDraft = result.data.find(d => d.status === 'ready_for_review');
                        setActiveDraft(readyDraft || null);
                    } else {
                        setActiveDraft(null);
                    }
                })
                .catch(() => setActiveDraft(null));
        } else {
            setActiveDraft(null);
        }
    }, [persona?.infoBudget?.budget_id, draftRefreshKey]);


    // Configuración de los pasos
    const steps = [
        {
            id: 0,
            label: 'Mesa de Control',
            icon: 'pi pi-shield',
            stateKey: 'mesaControl',
            independent: true  // No enlazado con los pasos anteriores
        },
        {
            id: 1,
            label: 'Aprobación',
            icon: 'pi pi-file-check',
            stateKey: 'confirmQuote'
        },
        {
            id: 2,
            label: 'Pago Inicial',
            icon: 'pi pi-upload',
            stateKey: 'uploadReceipt'
        },
        {
            id: 3,
            label: 'Orden de Compra',
            icon: 'pi pi-receipt',
            stateKey: 'requestInvoice'
        }
        // Seguros vive en DashboardHeader (no se duplica aquí).
    ];

    // TODO: En futuras actualizaciones, esta variable será el resultado de validar
    // que todos los pasos dentro de Mesa de Control estén completos (documentos revisados,
    // Creditrust aprobado, Buró consultado, Visita Ocular completada, etc.)
    // Por ahora se mantiene en true para no bloquear el flujo existente.
    const isMesaControlCompleted = true;


    // Función para obtener el ícono y color según el estado
    const getStepIconColor = (state) => {
        switch (state) {
            case 'completed': return { icon: 'pi pi-check-circle', color: '#16a34a' }; // Verde
            case 'in-progress': return { icon: 'pi pi-clock', color: '#f59e0b' }; // Naranja
            case 'requote': return { icon: 'pi pi-refresh', color: '#f59e0b' }; // Amarillo
            case 'error': return { icon: 'pi pi-times-circle', color: '#dc2626' }; // Rojo
            case 'unsuccessful': return { icon: 'pi pi-times-circle', color: '#dc2626' }; // Rojo
            default: return { icon: 'pi pi-circle', color: '#9ca3af' }; // Gris (pendiente)
        }
    };

    const getStepStatusLabel = (status) => {
        switch (status) {
            case 'completed': return 'Completado';
            case 'pending': return 'Pendiente';
            case 'in-progress': return 'En Progreso';
            case 'unsuccessful': return 'No exitosa';
            case 'requote': return 'Recotización';
            default: return 'Desconocido';
        }
    };

    const getStepStatusSeverity = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'in-progress': return 'info';
            case 'requote': return 'warning';
            default: return 'secondary';
        }
    };

    // Función para obtener el texto del estado
    const getStatusText = (state) => {
        switch (state) {
            case 'completed': return 'Completado';
            case 'in-progress': return 'En Proceso';
            case 'error': return 'Error';
            case 'unsuccessful': return 'No exitosa';
            case 'requote': return 'Recotización';
            default: return 'Pendiente';
        }
    };

    // Funciones de acción
    const handleConfirmQuote = () => {
        setStepStates(prev => ({ ...prev, confirmQuote: 'completed' }));
        setActiveStep(2); // Ir al siguiente paso (Pago Inicial)
    };

    const handleUploadReceipt = () => {
        setShowInitialPaymentModal(true);
    };

    const handleRequestInvoice = () => {
        setShowInvoiceModal(true);
    };

    const handleGoToApproval = () => {
        setShowInvoiceModal(false);
        setActiveStep(1);
        setAccordionOpen(0);
        setShowEmailModal(true);
    };

    const handleDraftDiscarded = () => {
        setActiveDraft(null);
    };

    const handleFileDeleted = () => {
        fetchStepStatus(persona, setStepStates, setStepsData, setLoading, setActiveStep, setProcessId);
    };

    const handleInvoiceSuccess = (processData) => {
        setShowInvoiceModal(false);
        processSteps(processData);
        setStepStates(prev => ({ ...prev, requestInvoice: 'completed' }));
    };

    const processSteps = (processData) => {
        const updatedSteps = processData.steps || [];
        setStepsData(updatedSteps);

        const newStates = {
            ocularVisit: 'pending',
            confirmQuote: 'pending',
            uploadReceipt: 'pending',
            requestInvoice: 'pending'
        };

        updatedSteps.forEach(step => {
            if (step.step_type === 'ocular_visit') {
                newStates.ocularVisit = step.status;
            } else if (step.step_type === 'confirm_quote') {
                newStates.confirmQuote = step.status;
            } else if (step.step_type === 'upload_payment_proof') {
                newStates.uploadReceipt = step.status;
            } else if (step.step_type === 'request_invoice') {
                newStates.requestInvoice = step.status;
            }
            // 'add_insurance' lo maneja el botón Seguros del Header — no se trackea aquí.
        });

        setStepStates(newStates);

        // Determine Active Step based on progress
        // Mesa de Control (0) is independent. Steps: Confirmar (1), Subir (2), Factura (3)
        // Visita Ocular ahora vive dentro de Mesa de Control, pero su estado sigue siendo
        // una condición para habilitar Aprobación.
        if (newStates.ocularVisit !== 'completed' || !isMesaControlCompleted) {
            setActiveStep(0); // Mantener en Mesa de Control
            setAccordionOpen(null);
        } else if (newStates.confirmQuote !== 'completed') {
            setActiveStep(1);
            setAccordionOpen(0);
        } else if (newStates.uploadReceipt !== 'completed') {
            setActiveStep(2);
            setAccordionOpen(0);
        } else if (newStates.requestInvoice !== 'completed') {
            setActiveStep(3);
            setAccordionOpen(0);
        } else {
            // All completed (Seguros se gestiona desde el botón del Header).
            setActiveStep(3);
            setAccordionOpen(0);
        }
    }

    const handleOcularSuccess = (processData) => {
        processSteps(processData);
    };

    const handleEmailSuccess = (processData) => {
        processSteps(processData);
        // Refresh draft badge: if a draft was just approved, badge should disappear
        if (onDraftChange) onDraftChange();
    };


    // Renderizar contenido según el paso activo
    const renderStepContent = () => {
        const stepIconOcular = getStepIconColor(stepStates.ocularVisit);
        const stepIconUpload = getStepIconColor(stepStates.uploadReceipt);
        const stepIconInvoice = getStepIconColor(stepStates.requestInvoice);

        // Verificar si faltan categorías
        const areCategoriesMissing = categoriasFaltantes && categoriasFaltantes.length > 0;
        const isMissingFinancialDetails = persona.infoBudget?.financial_details == null;
        const isOcularCompletedSuccessfully = stepStates.ocularVisit === 'completed';
        const isApprovalBlocked = false /* !isOcularCompletedSuccessfully || areCategoriesMissing || isMissingFinancialDetails; */
        const missingCategoriesTooltip = !isOcularCompletedSuccessfully
            ? "Completa la visita ocular exitosamente primero"
            : isMissingFinancialDetails
                ? "La cotización requiere detalles financieros, para generarlos deberías modificar la cotización"
                : areCategoriesMissing
                    ? `Faltan:\n- ${categoriasFaltantes.join("\n- ")}`
                    : null;


        switch (activeStep) {
            case 0:
                return null;
            case 1: {
                const confirmStepData = stepsData.find(s => s.step_type === 'confirm_quote');
                return (
                    <ConfirmQuoteStep
                        stepState={stepStates.confirmQuote}
                        stepData={confirmStepData}
                        onShowEmailModal={() => setShowEmailModal(true)}
                        onConfirmQuote={handleConfirmQuote}
                        getStatusText={getStatusText}
                        getStepIconColor={getStepIconColor}
                        disabled={isApprovalBlocked}
                        tooltip={missingCategoriesTooltip}
                    />
                );
            }
            case 2:
                return (
                    <>
                        <div className="step-content-box">
                            <i className="pi pi-upload text-6xl mb-3" style={{ color: '#21588C' }}></i>
                            <h3 className="m-0 mb-2">Pago Inicial</h3>
                            <p className="text-gray-600 text-center m-0">
                                Carga el comprobante del pago inicial realizado.
                            </p>
                            <div className={`step-status-badge status-${stepStates.uploadReceipt}`}>
                                <i className={stepIconUpload.icon}></i>
                                <span>{getStatusText(stepStates.uploadReceipt)}</span>
                            </div>
                        </div>
                        <div className="flex pt-4 justify-content-end gap-2">
                            <Button
                                label={stepStates.uploadReceipt === 'completed' ? 'Ver Información' : 'Subir'}
                                className="Button Green"
                                icon="pi pi-upload"
                                onClick={handleUploadReceipt}
                                disabled={stepStates.confirmQuote !== 'completed'}
                                tooltip={missingCategoriesTooltip}
                                tooltipOptions={{ position: 'top' }}
                                style={{ border: 'none', outline: 'none' }}
                            />
                        </div>
                    </>
                );
            case 3:
                return (
                    <div className="flex flex-column">
                        <div className="step-content-box">
                            <i className="pi pi-receipt text-6xl mb-3" style={{ color: '#21588C' }}></i>
                            <h3 className="m-0 mb-2">Orden de Compra</h3>
                            <p className="text-gray-600 text-center m-0">
                                Solicita la orden de compra correspondiente.
                            </p>
                            <div className={`step-status-badge status-${stepStates.requestInvoice}`}>
                                <i className={stepIconInvoice.icon}></i>
                                <span>{getStatusText(stepStates.requestInvoice)}</span>
                            </div>
                        </div>
                        <div className="flex pt-4 justify-content-end">
                            <Button
                                label={stepStates.requestInvoice === 'completed' ? "Ver Detalles" : "Solicitar Orden"}
                                icon="pi pi-envelope"
                                className={`Button ${stepStates.requestInvoice === 'completed' ? 'Green' : 'Green'}`}
                                onClick={handleRequestInvoice}
                                disabled={stepStates.uploadReceipt !== 'completed'}
                                tooltip={missingCategoriesTooltip}
                                tooltipOptions={{ position: 'top' }}
                                style={{ border: 'none', outline: 'none' }}
                            />
                        </div>

                        {/*   <RequestInvoiceModal
                            visible={showInvoiceModal}
                            onHide={() => setShowInvoiceModal(false)}
                            persona={persona}
                            stepStatus={stepStates.requestInvoice}
                            stepData={stepsData.find(s => s.step_type === 'request_invoice')}
                            onSuccess={handleEmailSuccess}
                            toast={toast}
                        /> */}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="col-12 p-0 pb-3 ">
            <div className="card">
                <style>
                    {`
                        .step-content-box {
                            border: 2px dashed #e5e7eb;
                            border-radius: 8px;
                            padding: 2rem;
                            background: #f9fafb;
                            min-height: 8rem;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                        }

                        .step-status-badge {
                            display: inline-flex;
                            align-items: center;
                            gap: 0.5rem;
                            padding: 0.5rem 1rem;
                            border-radius: 6px;
                            font-weight: 500;
                            margin-top: 1rem;
                        }

                        .status-completed {
                            background: #dcfce7;
                            color: #166534;
                        }

                        .status-in-progress {
                            background: #fef3c7;
                            color: #92400e;
                        }

                        .status-error {
                            background: #fee2e2;
                            color: #991b1b;
                        }

                        .status-pending {
                            background: #f3f4f6;
                            color: #6b7280;
                        }

                        .status-requote {
                            background: #fef3c7;
                            color: #92400e;
                        }

                        .steps-header-container {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            width: 100%;
                            gap: 0.5rem; /* Reduced gap */
                            flex-wrap: nowrap; /* Prevent wrapping for the main container */
                        }

                        .step-item {
                            display: flex;
                            align-items: center;
                            justify-content: center; /* Center content */
                            gap: 0.5rem;
                            padding: 0.5rem;
                            border-radius: 8px;
                            cursor: pointer;
                            transition: all 0.3s ease;
                            flex: 1; /* Allow equal distribution */
                            min-width: 0; /* Allow shrinking */
                            text-align: center; /* Center text */
                        }

                        .step-item:hover {
                            background: #f3f4f6;
                        }

                        .step-item.active i {
                            background: #3b82f6;
                            color: white !important;
                            border-radius: 50%;
                            padding: 0.2rem;
                        }

                        .step-connector {
                            flex: 0 0 1rem; /* Smaller connector */
                            height: 2px;
                            background: #e5e7eb;
                            min-width: 10px; /* Minimum width for connector */
                        }

                         /* Label styling */
                        .step-item .step-label {
                            font-weight: 500;
                            font-size: 0.875rem;
                            white-space: normal; /* Allow wrapping */
                            line-height: 1.2;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            display: -webkit-box;
                            -webkit-line-clamp: 2; /* Limit to 2 lines */
                            -webkit-box-orient: vertical;
                        }

                        /* Responsive Adjustments */
                        @media (max-width: 1100px) {
                             .step-item {
                                flex-direction: column; /* Stack icon and text */
                                gap: 0.25rem;
                                padding: 0.25rem;
                            }
                             .step-item .step-label {
                                font-size: 0.75rem; /* Smaller text */
                            }
                        }

                        @media (max-width: 768px) {
                            .step-item .step-label {
                                display: none; /* Hide text on mobile */
                            }
                            .step-item {
                                padding: 0.5rem;
                            }
                            .step-connector {
                                flex: 1; /* Expand connector on mobile to space out dots */
                            }
                        }

                        /* Accordion Overrides */
                        .p-accordion .p-accordion-header-link {
                            border-radius: 1rem !important;
                            padding: 1rem !important; /* Ensure padding shouldn't cut off content */
                        }

                        .p-accordion .p-accordion-content {
                            border-radius: 0 0 1rem 1rem !important;
                            border-top: none !important;
                            ${activeStep === 0 ? 'display: none !important;' : ''}
                        }

                        .p-accordion .p-accordion-tab {
                            border-radius: 1rem !important;
                            overflow: hidden;
                            margin-bottom: 1rem; /* Spacing between accordions if multiple */
                        }

                        .p-accordion {
                            border-radius: 1rem !important;
                        }

                    `}
                </style>

                <Accordion activeIndex={accordionOpen} onTabChange={(e) => setAccordionOpen(e.index)}>

                    <AccordionTab
                        header={
                            <div className="steps-header-container">
                                {steps.map((step, index) => {
                                    // Logic to determine if the step should be disabled
                                    let isDisabled = false;
                                    if (!step.independent) {
                                        // isMesaControlCompleted gates all subsequent steps
                                        if (!isMesaControlCompleted) isDisabled = true;
                                        if (step.id === 1 && stepStates.ocularVisit !== 'completed') isDisabled = true;
                                        if (step.id === 2 && stepStates.confirmQuote !== 'completed') isDisabled = true;
                                        if (step.id === 3 && stepStates.uploadReceipt !== 'completed') isDisabled = true;
                                    }

                                    return (
                                        <React.Fragment key={step.id}>
                                            <div
                                                className={`step-item ${activeStep === step.id ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setActiveStep(step.id);
                                                    setAccordionOpen(step.id === 0 ? null : 0);
                                                    if (step.id === 0) setMesaControlCollapsed(false);
                                                }}
                                                style={{
                                                    opacity: isDisabled ? 0.5 : 1,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <i
                                                    className={getStepIconColor(stepStates[step.stateKey]).icon}
                                                    style={{
                                                        color: isDisabled ? '#9ca3af' : getStepIconColor(stepStates[step.stateKey]).color,
                                                        fontSize: '1.25rem'
                                                    }}
                                                ></i>
                                                <span className="step-label">{step.label}</span>
                                                {step.stateKey === 'confirmQuote' && (activeDraft || stepStates.confirmQuote === 'requote') && (
                                                    <Tag
                                                        value={stepStates.confirmQuote === 'requote' ? 'Re-aprobación' : 'Recotización'}
                                                        severity="warning"
                                                        style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem', marginLeft: '0.25rem' }}
                                                    />
                                                )}
                                                {step.stateKey === 'uploadReceipt' && (() => {
                                                    // While draft is ready_for_review: show preview from activeDraft
                                                    const previewAction = activeDraft?.payment_impact?.action_needed;
                                                    if (previewAction && previewAction !== 'none') {
                                                        return (
                                                            <Tag
                                                                value={previewAction === 'register_surplus' ? 'Saldo a favor' : 'Pago ajustado'}
                                                                severity={previewAction === 'register_surplus' ? 'success' : 'warning'}
                                                                style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem', marginLeft: '0.25rem' }}
                                                            />
                                                        );
                                                    }
                                                    // After approval: show while shortfall is unresolved (step reopened, not yet completed)
                                                    const paymentStep = stepsData.find(s => s.step_type === 'upload_payment_proof');
                                                    const shortfall = Number(paymentStep?.metadata?.shortfall_amount || 0);
                                                    if (shortfall > 0 && paymentStep.status !== 'completed') {
                                                        return (
                                                            <Tag
                                                                value="Pago ajustado"
                                                                severity="warning"
                                                                style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem', marginLeft: '0.25rem' }}
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                            {index < steps.length - 1 && <div className="step-connector"></div>}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        }
                    >
                        {loading && activeStep !== 0 ? (
                            <div className="flex justify-content-center align-items-center p-5" style={{ minHeight: '15rem' }}>
                                <div className="flex flex-column align-items-center gap-3">
                                    <i className="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                                    <span className="text-gray-500 font-medium">Cargando estado del proceso...</span>
                                </div>
                            </div>
                        ) : activeStep !== 0 ? (
                            renderStepContent()
                        ) : null}
                    </AccordionTab>
                </Accordion>
            </div>

            {/* Nueva ventana de Mesa de Control */}
            <div className="flex-1 render-content mt-3 mb-3" style={{ width: '100%' }}>
                <Card
                    header={
                        <div
                            className="flex align-items-center  cursor-pointer select-none gap-2"

                            onClick={() => setMesaControlCollapsed(!mesaControlCollapsed)}
                            style={{ borderBottom: mesaControlCollapsed ? 'none' : '1px solid #e0e0e0', padding: '1rem' }}
                        >
                            <Button
                                icon={`pi pi-chevron-${mesaControlCollapsed ? 'down' : 'up'}`}
                                rounded
                                text
                                aria-label="Toggle"
                                className="p-button-secondary p-0"
                                style={{ width: '2rem', height: '2rem', left: '-8px' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMesaControlCollapsed(!mesaControlCollapsed);
                                }}
                            />
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-shield text-xl" style={{ color: '#21588C' }}></i>
                                <span className="text-lg font-bold text-700">Mesa de Control</span>
                            </div>
                        </div>
                    }
                    pt={{
                        body: { className: 'p-0' },
                        content: { className: 'p-0' }
                    }}
                >
                    <div
                        className="overflow-hidden transition-all duration-300"
                        style={{
                            /*   maxHeight: mesaControlCollapsed ? '0px' : '2000px', */
                            maxHeight: mesaControlCollapsed ? '0px' : 'fit-content',
                            opacity: mesaControlCollapsed ? 0 : 1
                        }}
                    >
                        <div className="p-3">
                            {loading ? (
                                <div className="flex justify-content-center align-items-center p-5" style={{ minHeight: '15rem' }}>
                                    <div className="flex flex-column align-items-center gap-3">
                                        <i className="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                                        <span className="text-gray-500 font-medium">Cargando opciones de mesa de control...</span>
                                    </div>
                                </div>
                            ) : (
                                <MesaControlPanel
                                    persona={persona}
                                    toast={toast}
                                    dataBuro={dataBuro}
                                    categories={categories}
                                    onOcularSuccess={handleOcularSuccess}
                                    processId={processId}
                                    ocularStepStatus={stepStates.ocularVisit}
                                    ocularStepData={stepsData.find(s => s.step_type === 'ocular_visit')}
                                />
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            {/* Modal de correo de confirmación */}
            {showEmailModal && (
                <EmailConfirmationModal
                    visible={showEmailModal}
                    onHide={() => setShowEmailModal(false)}
                    initialEmail={persona.email}
                    persona={persona}
                    toast={toast}
                    stepStatus={stepStates.confirmQuote}
                    stepData={stepsData.find(s => s.step_type === 'confirm_quote')}
                    onSuccess={handleEmailSuccess}
                />
            )}

            {/* Modal de pago inicial */}
            {showInitialPaymentModal && (
                <InitialPaymentModal
                    visible={showInitialPaymentModal}
                    onHide={() => setShowInitialPaymentModal(false)}
                    persona={persona}
                    processId={processId}
                    toast={toast}
                    stepStatus={stepStates.uploadReceipt}
                    stepData={stepsData.find(s => s.step_type === 'upload_payment_proof')}
                    onSuccess={handleEmailSuccess}
                />
            )}
            {showInvoiceModal && (
                <RequestInvoiceModal
                    visible={showInvoiceModal}
                    onHide={() => setShowInvoiceModal(false)}
                    persona={persona}
                    stepStatus={stepStates.requestInvoice}
                    stepData={stepsData.find(s => s.step_type === 'request_invoice')}
                    onSuccess={handleInvoiceSuccess}
                    toast={toast}
                    onOpenRecotizacion={(context) => {
                        // Inject processId into context before passing up
                        if (onOpenRecotizacion) {
                            onOpenRecotizacion({ ...context, processId });
                        }
                    }}
                    onGoToApproval={handleGoToApproval}
                    onDraftDiscarded={handleDraftDiscarded}
                    onFileDeleted={handleFileDeleted}
                />
            )}
            {/* Seguros se monta desde DashboardHeader (no se duplica aquí). */}
        </div>
    );
};